import { Text as LegacyText } from '@chakra-ui/react';
import {
  AppleIcon,
  Box,
  BoxProps,
  Button,
  Container,
  Flex,
  keyframes,
  Link,
  Text,
} from '@joinrepublic/dls-ui-kit';

import { TRegionValidIds } from '@r-client/shared/feature/region';

import { GooglePlayStoreLogoOnlyIcon } from '../../assets/icons/google-play-store-logo-only-icon';
import { colors } from '../../config/styles';
import { containerSize } from '../../config/styles';
import { DesktopCategoryLists } from './desktop-category-lists';
import { MobileCategoryLists } from './mobile-category-lists';

export interface ITopSectionProps {
  region?: TRegionValidIds;
}

// The hero is the page's LCP element, so its entrance animations are plain CSS: they start on first paint
// instead of waiting for hydration the way the framer-motion `MotionBox` does.
const fadeSlideIn = (distance: string) => keyframes`
  from { opacity: 0; transform: translateY(${distance}); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const headingMobileSlide = fadeSlideIn('0.5em');
const headingDesktopSlide = fadeSlideIn('1em');
const blockSlide = fadeSlideIn('30px');

const desktopEasing = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

const reduceMotion: BoxProps = {
  _mediaReduceMotion: { animation: 'none' },
};

export const TopSection = ({ region = 'us' }: ITopSectionProps) => {
  const isGlobal = region === 'global';
  // Note: The font-size is calculated based on the character count of the title. If the title changes, the font size may need to be adjusted.
  const title = isGlobal
    ? ['Innovation through', 'tokenization']
    : ['Private markets,', 'public access'];

  return (
    <Box
      as="section"
      // TODO: https://linear.app/republic/issue/USMKT-1457/change-all-hardcoded-colours-for-the-new-hp-to-the-proper-tokens
      bg={colors.darkNavy}
      color="white"
      pt={{ base: '7', md: '24' }}
      pb={{ base: '20', md: '14' }}
      borderTop="1px solid"
      borderColor="whiteAlpha.400"
    >
      <Container maxW={containerSize}>
        <Box position="relative">
          <Box>
            <LegacyText
              as="h1"
              fontSize={{
                base: isGlobal
                  ? 'calc(1.5rem + (18.85svw - 32px))'
                  : 'calc(1.5rem + (25svw - 32px))',
                sm: '100px',
              }}
              fontWeight="500"
              lineHeight="0.95em"
              letterSpacing="-0.05em"
              color="inherit"
            >
              {title.map((line, index) => (
                // 0.2m is a fix for the text being cut off due to the negative letter spacing
                <Box
                  key={index}
                  overflow={{ base: 'visible', sm: 'hidden' }}
                  pb="0.2em"
                  mb="-0.2em"
                >
                  <Box
                    animation={{
                      base: `${headingMobileSlide} 1.2s ease-out both`,
                      sm: `${headingDesktopSlide} 1.2s ${desktopEasing} ${
                        index * 0.15
                      }s both`,
                    }}
                    {...reduceMotion}
                  >
                    {line.split(' ').map((word, wordIndex) => (
                      <Box
                        key={wordIndex}
                        as="span"
                        display={{ base: 'block', md: 'inline' }}
                        whiteSpace="nowrap"
                      >
                        {word}{' '}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </LegacyText>

            <Box
              animation={`${blockSlide} 1s ease-out 0.2s both`}
              {...reduceMotion}
            >
              <Text
                mt={{ base: '5', md: '10' }}
                maxW={{ base: '330px', sm: '486px' }}
                size="body-lg"
                color="blue.50"
              >
                Republic is an on-chain investment platform redefining global
                accessibility to the private markets.
              </Text>
              <Button
                as="a"
                href="/register?after_sign_in=/investment-opportunities"
                variant="primary"
                mt={{ base: '8', sm: '12' }}
                w={{ base: 'full', sm: 'fit-content' }}
                minW="40"
              >
                Get started
              </Button>
            </Box>
          </Box>

          <Box
            animation={{
              base: `${blockSlide} 0.8s ease-out 0.5s both`,
              sm: `${blockSlide} 1.1s ease-out 0.3s both`,
            }}
            {...reduceMotion}
            mt={{ base: '20', md: '32' }}
          >
            <Box display={{ base: 'block', sm: 'none' }}>
              <MobileCategoryLists />
            </Box>
            <Box display={{ base: 'none', sm: 'block' }}>
              <DesktopCategoryLists />
            </Box>
          </Box>

          <Box
            animation={`${fadeIn} 1.1s ease-out 0.4s both`}
            {...reduceMotion}
          >
            <RightSideIcons />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
const RightSideIcons = () => (
  <Flex
    position="absolute"
    top="8"
    right="0"
    align="center"
    gap="4"
    display={{ base: 'none', md: 'flex' }}
  >
    <Link
      href="https://apps.apple.com/us/app/republic-invest-in-startups/id1475230022"
      isExternal={true}
      aria-label="iOS Republic App"
    >
      <Flex
        w="12"
        h="12"
        border="1px solid"
        borderColor="blue.100"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        _hover={{ borderColor: 'white' }}
      >
        <AppleIcon color="white" />
      </Flex>
    </Link>
    <Link
      href="https://play.google.com/store/apps/details?id=com.republicmobile&hl=en"
      isExternal={true}
      aria-label="Android Republic App"
    >
      <Flex
        w="12"
        h="12"
        border="1px solid"
        borderColor="blue.100"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        _hover={{ borderColor: 'white' }}
      >
        <GooglePlayStoreLogoOnlyIcon color="white" />
      </Flex>
    </Link>
  </Flex>
);
